import React, { useCallback, useContext, useEffect, useState } from 'react';
import { CardHeader, CardSection, useModal } from '@europrocurement/flexy-components';
import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import { useDownload } from '@europrocurement/l2d-modules/hooks';
import { enqueueSnackbar } from 'notistack';
import { CollectionDeDossierJsonldCollectiondossierRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';

import { useModalMail } from '@europrocurement/l2d-modules/modules/MailForm';
import { usePanierPay } from '@europrocurement/l2d-modules/modules/Paiement';
import useDossierCollection from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierCollection';
import useExportDossier from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useExportDossier';
import {
    BillingContext,
    DocumentPreviewContext,
    DossierPrescriberContext,
    SelectedDossierContext,
} from '../../../providers';
import { useCollectionDossier } from '../../../hooks';
import { InvoiceItemProps, InvoiceStatus } from '../InvoiceItem/InvoiceItem';
import BillingInfo from '../BillingInfo/BillingInfo';
import InvoiceList from '../InvoiceList/InvoiceList';
import InvoiceProvisionPayment from '../InvoiceProvisionPayment/InvoiceProvisionPayment';
import NoResult from '../NoResult/NoResult';
import ModalPaiement from '../ModalPaiement/ModalPaiement';
import useCollectionProvision, {
    CollectionProvisionType,
} from '../../../hooks/useCollectionProvision';
import { UpdateCollectionPayload } from '../../../types/UpdateCollectionPayload';
import useDossierPrescripteur from '../../../hooks/useDossierPrescripteur';
import useBilling from '../../../hooks/useBilling';

const BillingTab: React.FunctionComponent = function () {
    const { dossier } = useContext(SelectedDossierContext);
    const { getDossierCollection } = useDossierCollection();
    const { updateCollections } = useCollectionDossier();

    const { searchCollectionProvision } = useCollectionProvision();

    const { setDocumentPreview } = useContext(DocumentPreviewContext);
    const { prescriber, setPrescriberId, sousClients, prescriberLoaded } =
        useContext(DossierPrescriberContext);
    const { estimates, invoices, estimatesLoaded, invoicesLoaded, setCollectionsIds } =
        useContext(BillingContext);
    const { getBillingAddress } = useDossierPrescripteur();
    const { download } = useDownload();
    const {
        getEstimateLabel,
        getInvoiceLabel,
        isInvoicePaid,
        getInvoiceRemainingAmount,
        downloadInvoice,
        downloadEstimate,
        canUpdateBillingInfos,
    } = useBilling({});
    const [collection, setCollection] = useState<CollectionDeDossierJsonldCollectiondossierRead>();
    const clientId = dossier?.clientId as number;
    const clientOpereId = dossier?.clientOpereId as number;
    const [defaultIndirectSale, setDefaultIndirectSale] = useState<boolean>();

    const prescriberId = dossier?.prescripteurId as number;
    const [recipientId, setRecipientId] = useState<number>(clientId);
    const [indirectSale, setIndirectSale] = useState<boolean>();
    const [invoiceItems, setInvoiceItems] = useState<Array<InvoiceItemProps>>([]);
    const [totalRemainingAmount, setTotalRemainingAmount] = useState<number>(0);
    const [invoiceToPayItems, setInvoiceToPayItems] = useState<Array<number>>([]);

    const clientAddresses = sousClients.map((client) => ({
        clientId: client.id as number,
        label: client.raisonSociale,
        siren: client.siren as string,
        address: getBillingAddress(client),
    }));
    const hasClientAddress =
        prescriberId === clientId ||
        sousClients.find((sousClient) => sousClient.id === clientId) !== undefined;

    if (!hasClientAddress) {
        clientAddresses.push({
            clientId,
            label: dossier?.clientLibelle as string,
            siren: dossier?.clientSiren as string,
            address: dossier?.clientAdresseComplete as string,
        });
    }

    const handleRecipientChange = (newRecipientId?: number) => {
        setRecipientId(
            newRecipientId ||
                (prescriberId !== clientOpereId ? clientOpereId : (sousClients[0].id as number)),
        );

        // Add notification to user.
        enqueueSnackbar('Le client facturé a bien été modifié.', {
            variant: 'success',
        });
    };

    const handleIndirectSaleChange = (newIndirectSale: boolean) => {
        setIndirectSale(newIndirectSale);

        // Add notification to user.
        enqueueSnackbar('Le choix du recouvrement a bien été modifié.', {
            variant: 'success',
        });
    };

    const handleDownloadEstimate = (fileName: string, id: number): Promise<void> =>
        new Promise((resolve, reject) => {
            downloadEstimate(id)
                .then((fileContent: Blob) => {
                    download(fileName, fileContent);

                    resolve(undefined);
                })
                .catch(reject);
        });

    const handleShowEstimate = (fileName: string, id: number): Promise<void> =>
        new Promise((resolve, reject) => {
            downloadEstimate(id)
                .then((fileContent: Blob) => {
                    setDocumentPreview({
                        fileName,
                        fileType: 'pdf',
                        content: fileContent,
                    });

                    resolve(undefined);
                })
                .catch(reject);
        });

    const handleDownloadInvoice = (fileName: string, id: number): Promise<void> =>
        new Promise((resolve, reject) => {
            downloadInvoice(id)
                .then((fileContent: Blob) => {
                    download(fileName, fileContent);

                    resolve(undefined);
                })
                .catch(reject);
        });

    const handleShowInvoice = (fileName: string, id: number): Promise<void> =>
        new Promise((resolve, reject) => {
            downloadInvoice(id)
                .then((fileContent: Blob) => {
                    setDocumentPreview({
                        fileName,
                        fileType: 'pdf',
                        content: fileContent,
                    });

                    resolve(undefined);
                })
                .catch(reject);
        });

    useEffect(() => {
        setPrescriberId(prescriberId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prescriberId]);

    useEffect(() => {
        const payload: UpdateCollectionPayload = {
            clientId: undefined,
            venteIndirecte: false,
        };

        if (clientId !== recipientId) {
            payload.clientId = recipientId;
        }
        if (!collection || !collection?.id) {
            return;
        }

        if (prescriberId === recipientId) {
            payload.venteIndirecte = false;
        } else {
            const sousClient = sousClients.find((t) => t.id === recipientId);

            if (sousClient) {
                payload.venteIndirecte = sousClient.venteIndirecte;
            }
        }

        const newIndirectSale = payload.venteIndirecte || defaultIndirectSale;
        const newClientId = payload.clientId || clientId;
        const hasChanges = newIndirectSale !== indirectSale || newClientId !== clientId;

        if (!hasChanges) {
            return;
        }

        if (newIndirectSale !== indirectSale) {
            setIndirectSale(newIndirectSale);
        }

        updateCollections([collection?.id], {
            clientId: payload.clientId || clientId,
            venteIndirecte: newIndirectSale,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recipientId]);

    useEffect(() => {
        if (!collection || !collection?.id) {
            return;
        }
        if (defaultIndirectSale !== indirectSale) {
            setDefaultIndirectSale(indirectSale);
            updateCollections([collection?.id], { venteIndirecte: indirectSale });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [indirectSale]);

    useEffect(() => {
        setInvoiceToPayItems([
            ...invoices
                .filter((item) => item.paye === false)
                .map((estimate) => estimate.id as number),
        ]);
        setInvoiceItems([
            ...estimates.map((estimate) => ({
                label: getEstimateLabel(estimate),
                status: 'paid' as InvoiceStatus,
                title: 'le devis',
                id: estimate.id as number,
                onDownload: handleDownloadEstimate,
                onShow: handleShowEstimate,
            })),
            ...invoices.map((invoice) => ({
                label: getInvoiceLabel(invoice),
                status: (isInvoicePaid(invoice) ? 'paid' : 'pending') as InvoiceStatus,
                title: 'la facture',
                feedback: isInvoicePaid(invoice) ? 'Réglé' : 'À régler',
                id: invoice.id as number,
                onDownload: handleDownloadInvoice,
                onShow: handleShowInvoice,
            })),
        ]);
        setTotalRemainingAmount(
            invoices
                .filter((item) => item.paye === false && item.factureEnPreparation === false)
                .reduce((prev, current) => prev + getInvoiceRemainingAmount(current), 0),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [estimates, invoices]);

    const allDataLoaded = prescriberLoaded && estimatesLoaded && invoicesLoaded && collection;
    let billingBody = (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
            <CircularProgress />
        </Box>
    );

    const { modalActions } = useModal();

    const { showMailModal } = useModalMail();
    const { searchContacts } = useExportDossier();

    const { bodyMailCollection, bodyMailFacture } = usePanierPay();

    const onClickDemandeReglement = useCallback(
        async (type: string) => {
            try {
                const contact = await searchContacts('', [recipientId]);
                if (!contact || contact.length === 0) {
                    enqueueSnackbar('Aucun contact trouvé pour le client sélectionné', {
                        variant: 'error',
                    });
                    return;
                }
                if (type === 'facture' && collection !== undefined) {
                    const builtAttachments = await Promise.all(
                        invoiceToPayItems.map(async (id) => {
                            const blob = await downloadInvoice(id);
                            const invoice = invoices.find((inv) => inv.id === id);
                            const filename = invoice
                                ? `${getInvoiceLabel(invoice)}.pdf`
                                : `facture_${id}.pdf`;
                            return {
                                file: new File([blob], filename, { type: 'application/pdf' }),
                            };
                        }),
                    );
                    if (!prescriber) {
                        enqueueSnackbar('Prescripteur non trouvé', {
                            variant: 'error',
                        });
                    } else {
                        const body: string = await bodyMailFacture(
                            `${contact[0].firstName} ${contact[0].lastName}`,
                            collection,
                            totalRemainingAmount.toString(),
                            invoiceToPayItems,
                        );
                        showMailModal({
                            to: [
                                {
                                    emailAddress: contact[0].emailAddress,
                                    firstName: contact[0].firstName,
                                    lastName: contact[0].lastName,
                                },
                            ],
                            subject: `Demande de règlement`,
                            showTo: true,
                            body,
                            searchParamsTiersIds: [prescriber.id as number],
                            showBody: true,
                            showAttachments: true,
                            attachments: [...builtAttachments],
                        });
                    }
                } else if (type === 'collection' && collection !== undefined && collection.id) {
                    if (!prescriber) {
                        enqueueSnackbar('Prescripteur non trouvé', {
                            variant: 'error',
                        });
                    } else {
                        const body: string = await bodyMailCollection(
                            `${contact[0].firstName} ${contact[0].lastName}`,
                            collection,
                        );
                        showMailModal({
                            to: [
                                {
                                    emailAddress: contact[0].emailAddress,
                                    firstName: contact[0].firstName,
                                    lastName: contact[0].lastName,
                                },
                            ],
                            subject: `Demande de provision`,
                            showTo: true,
                            body,
                            searchParamsTiersIds: [prescriber.id as number],
                            showBody: true,
                            showAttachments: true,
                        });
                    }
                } else {
                    console.log('Pas de donnée à mettre dans le panier');
                    enqueueSnackbar('Une erreur est survenue', {
                        variant: 'error',
                    });
                }
            } catch (error) {
                console.log(error);
                enqueueSnackbar('Une erreur est survenue', {
                    variant: 'error',
                });
            }
        },
        [
            searchContacts,
            recipientId,
            collection,
            invoiceToPayItems,
            bodyMailFacture,
            totalRemainingAmount,
            showMailModal,
            prescriber,
            downloadInvoice,
            invoices,
            getInvoiceLabel,
            bodyMailCollection,
        ],
    );

    const onClickPaiement = useCallback(
        (amount: number, ids: Array<number>, type: 'collection' | 'facture') => {
            modalActions.call(
                <ModalPaiement
                    ids={ids}
                    amount={amount}
                    type={type}
                    dossierNumero={dossier?.numero as string}
                />,
            );
        },
        [dossier?.numero, modalActions],
    );

    const [acomptes, setAcomptes] = useState<Array<CollectionProvisionType>>([]);
    useEffect(() => {
        if (dossier && dossier.collectionId) {
            setCollectionsIds([dossier.collectionId]);
            getDossierCollection(dossier.collectionId as number).then(setCollection);
            searchCollectionProvision([dossier.collectionId]).then(setAcomptes);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier]);
    useEffect(() => {
        if (collection) {
            setDefaultIndirectSale(collection?.venteIndirecte);
            setIndirectSale(collection?.venteIndirecte);
        }
    }, [collection]);

    const totalAcomptes = parseFloat(
        acomptes
            .reduce(
                (total, acompte) => total + (acompte.montant ? parseFloat(acompte.montant) : 0),
                0,
            )
            .toFixed(2),
    );

    const wrapperInvoicePaymentSx = {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    };

    const labelSx = {
        fontSize: '15px',
        fontWeight: '600',
        lineHeight: '21px',
    };

    const amountLabelSx = {
        fontSize: '22px',
        fontWeight: '600',
        lineHeight: '26.4px',
    };

    if (allDataLoaded) {
        billingBody = (
            <>
                {prescriber && indirectSale !== undefined && (
                    <BillingInfo
                        prescriberId={prescriberId}
                        recipientId={recipientId}
                        prescriberLabel={dossier.prescripteurLibelle as string}
                        prescriberSiren={dossier.prescripteurSiren as string}
                        prescriberAddress={getBillingAddress(prescriber)}
                        onRecipientChange={handleRecipientChange}
                        addresses={clientAddresses}
                        billingType={indirectSale}
                        onBillingTypeChange={handleIndirectSaleChange}
                        canEdit={canUpdateBillingInfos(invoices)}
                        canEditBillingType
                    />
                )}
                {invoiceItems.length > 0 ? (
                    <>
                        <Divider />
                        <InvoiceList items={invoiceItems} />
                        {invoiceToPayItems.length > 0 && totalRemainingAmount > 0 && (
                            <InvoiceProvisionPayment
                                amount={totalRemainingAmount}
                                isFacture
                                onClickDemandeReglement={() => onClickDemandeReglement('facture')}
                                onClickPaiement={() =>
                                    onClickPaiement(
                                        totalRemainingAmount,
                                        invoiceToPayItems,
                                        'facture',
                                    )
                                }
                            />
                        )}
                    </>
                ) : (
                    <NoResult
                        paddingSx="80px"
                        title="Aucune facture disponible pour le moment"
                        description="La commande est en attente d'estimation"
                    />
                )}

                {invoices.length === 0 &&
                collection &&
                collection?.acompteAttendu &&
                collection?.acompteAttendu !== undefined &&
                (collection.acompteAttendu as unknown as number) - totalAcomptes > 0 ? (
                    <>
                        <Divider />
                        <InvoiceProvisionPayment
                            amount={
                                (collection.acompteAttendu as unknown as number) - totalAcomptes
                            }
                            onClickDemandeReglement={() => onClickDemandeReglement('collection')}
                            onClickPaiement={() =>
                                collection.id !== undefined &&
                                onClickPaiement(
                                    (collection.acompteAttendu as unknown as number) -
                                        totalAcomptes,
                                    [collection.id as number],
                                    'collection',
                                )
                            }
                        />
                    </>
                ) : null}
                {totalAcomptes && totalAcomptes > 0 ? (
                    <>
                        <Divider />
                        <Box
                            sx={wrapperInvoicePaymentSx}
                            data-testid="invoice-payment"
                        >
                            <Typography sx={labelSx}>Provision (TTC) réglée</Typography>

                            <Typography sx={amountLabelSx}>
                                {(Math.floor(totalAcomptes * 100) / 100).toFixed(2)}&euro;
                            </Typography>
                        </Box>
                    </>
                ) : null}
            </>
        );
    }

    return (
        <CardSection
            header={
                <CardHeader
                    title="Facturation"
                    titleSx={{
                        fontSize: '20px',
                        lineHeight: '28px',
                    }}
                    border={false}
                />
            }
            sxContent={{
                padding: '8px 32px 32px',
                display: 'flex',
                flexDirection: 'column',
                gap: '32px 0',
            }}
        >
            {billingBody}
        </CardSection>
    );
};

export default BillingTab;
